// What the AI read of an uploaded document should pull out of it, per document
// type (PET-4000). Pure data, so the worker builds the model's output schema from
// it and the web apps render the same fields for review without importing any
// model code.

import { canonicalDocumentSlug } from './fleet-documents'
import { ISO_DATE_REGEX } from './time'

// The four tables a document version can live in. An extraction row points at
// one of them by `(store, documentId)`.
export const DOCUMENT_EXTRACTION_STORES = ['onboarding', 'driver', 'truck', 'carrier'] as const
export type DocumentExtractionStore = (typeof DOCUMENT_EXTRACTION_STORES)[number]

export const DOCUMENT_EXTRACTION_STATUSES = ['pending', 'running', 'succeeded', 'failed'] as const
export type DocumentExtractionStatus = (typeof DOCUMENT_EXTRACTION_STATUSES)[number]

// Who put the value in a document's `expiration_date` (DOC-47, DOC-48). Only an
// `extracted` date is still the model's word: the office has not looked at it.
export const EXPIRATION_DATE_SOURCES = ['manual', 'extracted', 'confirmed'] as const
export type ExpirationDateSource = (typeof EXPIRATION_DATE_SOURCES)[number]

// The expiry columns for a date a person typed, or cleared.
export const manualExpiry = (
  expirationDate: string | null,
): { expirationDate: string | null; expirationDateSource: ExpirationDateSource | null } => ({
  expirationDate,
  expirationDateSource: expirationDate ? 'manual' : null,
})

// `date` is an ISO YYYY-MM-DD string, `money` a whole-dollar amount, `last4` the
// last four digits of a number we refuse to store whole (DOC-50).
export const DOCUMENT_EXTRACTION_FIELD_KINDS = [
  'text',
  'date',
  'money',
  'number',
  'boolean',
  'textList',
  'last4',
] as const
export type DocumentExtractionFieldKind = (typeof DOCUMENT_EXTRACTION_FIELD_KINDS)[number]

export type DocumentExtractionField = {
  key: string
  kind: DocumentExtractionFieldKind
  // Told to the model. English, and about the paper, not the UI: labels come
  // from the `documents` locale namespace under `extraction.fields.<key>`.
  hint: string
}

export type DocumentExtractionTemplate = {
  // Canonical slug (fleet vocabulary), or `generic` for the fallback.
  slug: string
  // Bumped whenever the fields or the wording change, so a stored read says
  // which questions it answered.
  version: number
  // One line on what the paper is, for the prompt.
  describes: string
  fields: readonly DocumentExtractionField[]
  // Which field feeds the document's `expiration_date`, if any.
  expirationField: string | null
  // A document with no printed expiry that expires a fixed time after a printed
  // date (an annual DOT inspection). Computed in code, never asked of the model.
  derivedExpiration?: { fromField: string; months: number }
}

// For now a read asks only for the two dates every compliance document is
// judged by (PET-4000): when it took effect and when it lapses. The hints are what tell the model which printed date
// counts for each type; other fields come back as entries in a template's
// `fields`, which the worker, the API and the review panel already render.
const datesOf = (hints: { effective: string; expiration: string }) =>
  [
    { key: 'effectiveDate', kind: 'date', hint: hints.effective },
    { key: 'expirationDate', kind: 'date', hint: hints.expiration },
  ] as const satisfies readonly DocumentExtractionField[]

// Any document may carry an expiry, even a type that usually prints none: the
// read leaves it null then, and the office can still type one.
const NONE_PRINTED = 'Expiration date, only if one is printed.'

export const DOCUMENT_EXTRACTION_TEMPLATES: Readonly<Record<string, DocumentExtractionTemplate>> = {
  insurance_certificate: {
    slug: 'insurance_certificate',
    version: 2,
    describes:
      'a certificate of liability insurance (usually an ACORD 25) for a trucking company or owner operator',
    // The auto liability policy is what a truck is dispatched under; the
    // form's other lines of coverage often run on different dates.
    fields: datesOf({
      effective: 'Effective date of the automobile liability policy.',
      expiration: 'Expiration date of the automobile liability policy.',
    }),
    expirationField: 'expirationDate',
  },
  workers_comp_certificate: {
    slug: 'workers_comp_certificate',
    version: 2,
    describes: "a certificate of workers' compensation insurance",
    fields: datesOf({
      effective: "Effective date of the workers' compensation policy.",
      expiration: "Expiration date of the workers' compensation policy.",
    }),
    expirationField: 'expirationDate',
  },
  workers_comp_exemption: {
    slug: 'workers_comp_exemption',
    version: 2,
    describes: "a workers' compensation exemption certificate",
    fields: datesOf({
      effective: 'Date the exemption takes effect.',
      expiration: 'Date the exemption expires.',
    }),
    expirationField: 'expirationDate',
  },
  cdl: {
    slug: 'cdl',
    version: 2,
    describes: "a US commercial driver's license (front and back may be separate images)",
    fields: datesOf({
      effective: 'Issue date of the license.',
      expiration: 'Expiration date of the license.',
    }),
    expirationField: 'expirationDate',
  },
  medical_card: {
    slug: 'medical_card',
    version: 2,
    describes: "a DOT medical examiner's certificate (medical card)",
    fields: datesOf({
      effective: 'Date the medical examination was performed.',
      expiration: 'Date the medical certificate expires.',
    }),
    expirationField: 'expirationDate',
  },
  registration: {
    slug: 'registration',
    version: 2,
    describes: 'a vehicle registration card',
    fields: datesOf({
      effective: 'Issue date of the registration.',
      expiration: 'Expiration date of the registration.',
    }),
    expirationField: 'expirationDate',
  },
  dot_inspection: {
    slug: 'dot_inspection',
    version: 2,
    describes: 'an annual DOT vehicle inspection report',
    fields: datesOf({
      effective: 'Date the inspection was performed.',
      expiration: 'Expiration date, only if one is printed.',
    }),
    expirationField: 'expirationDate',
    // An annual inspection is good for 12 months (49 CFR 396.17) and the
    // report rarely prints an expiry of its own.
    derivedExpiration: { fromField: 'effectiveDate', months: 12 },
  },
  w9: {
    slug: 'w9',
    version: 3,
    describes: 'an IRS Form W-9',
    fields: datesOf({ effective: 'Date the form was signed.', expiration: NONE_PRINTED }),
    expirationField: 'expirationDate',
  },
  voided_check: {
    slug: 'voided_check',
    version: 3,
    describes: 'a voided bank check used to set up payments',
    fields: datesOf({
      effective: 'Date printed or written on the check.',
      expiration: NONE_PRINTED,
    }),
    expirationField: 'expirationDate',
  },
  sunbiz_filing: {
    slug: 'sunbiz_filing',
    version: 3,
    describes: 'a Florida Division of Corporations (Sunbiz) entity detail page',
    fields: datesOf({ effective: 'Date the entity was filed.', expiration: NONE_PRINTED }),
    expirationField: 'expirationDate',
  },
  safer_snapshot: {
    slug: 'safer_snapshot',
    version: 3,
    describes: 'an FMCSA SAFER company snapshot',
    fields: datesOf({
      effective: 'Date the snapshot was taken (information as of).',
      expiration: NONE_PRINTED,
    }),
    expirationField: 'expirationDate',
  },
}

// Anything without a template of its own, tenant custom types included.
export const GENERIC_DOCUMENT_EXTRACTION_TEMPLATE: DocumentExtractionTemplate = {
  slug: 'generic',
  version: 2,
  describes: 'a compliance document submitted by a trucking company or driver',
  fields: datesOf({
    effective: 'Date the document takes effect or was issued.',
    expiration: 'Date the document expires. Null when no expiry is printed.',
  }),
  expirationField: 'expirationDate',
}

// Our own output (packets, archives, signed agreements): nothing to read back.
const NOT_EXTRACTED_SLUGS: ReadonlySet<string> = new Set([
  'broker_mc',
  'carrier_docs',
  'driver_docs',
  'signed_agreement',
])

export function isExtractableDocumentSlug(slug: string): boolean {
  return !NOT_EXTRACTED_SLUGS.has(slug)
}

export type DocumentExtractionValue = string | number | boolean | string[] | null

// The model's read of one document version. `values` is keyed by the template's
// field keys; every value is nullable because an absent or illegible field must
// come back null, not guessed.
export type DocumentExtractionFields = {
  values: Record<string, DocumentExtractionValue>
  detectedDocumentType: string | null
  matchesExpectedType: boolean | null
  legible: boolean | null
  lowConfidenceFields: string[]
  notes: string
}

export const DOCUMENT_EXTRACTION_WARNING_CODES = [
  'type_mismatch',
  'illegible',
  'already_expired',
] as const
export type DocumentExtractionWarningCode = (typeof DOCUMENT_EXTRACTION_WARNING_CODES)[number]

export type DocumentExtractionWarning = {
  code: DocumentExtractionWarningCode
  expected?: string
  found?: string
}

/**
 * One value as it may be stored, whoever wrote it: the model or a reviewer. A
 * date the reviewer can't parse is no better than none, and for a `last4` field
 * only the last four digits survive, whatever came in (DOC-50).
 */
export function normalizeExtractionValue(
  kind: DocumentExtractionFieldKind,
  value: unknown,
): DocumentExtractionValue {
  switch (kind) {
    case 'date':
      return typeof value === 'string' && ISO_DATE_REGEX.test(value) ? value : null
    case 'money':
    case 'number':
      return typeof value === 'number' && Number.isFinite(value) ? value : null
    case 'boolean':
      return typeof value === 'boolean' ? value : null
    case 'textList':
      return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : []
    case 'last4': {
      const digits = (typeof value === 'string' ? value : '').replace(/\D/g, '')
      return digits.length >= 4 ? digits.slice(-4) : null
    }
    case 'text':
      return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
  }
}

// What a reviewer sent, reduced to the template's own fields, each normalized.
// Keys the template does not ask for are dropped, never stored.
export function normalizeReviewedValues(
  template: DocumentExtractionTemplate,
  values: Readonly<Record<string, unknown>>,
): Record<string, DocumentExtractionValue> {
  return Object.fromEntries(
    template.fields
      .filter((field) => field.key in values)
      .map((field) => [field.key, normalizeExtractionValue(field.kind, values[field.key])]),
  )
}

export function documentExtractionTemplateFor(slug: string): DocumentExtractionTemplate {
  return (
    DOCUMENT_EXTRACTION_TEMPLATES[canonicalDocumentSlug(slug)] ??
    GENERIC_DOCUMENT_EXTRACTION_TEMPLATE
  )
}

// The date a set of values puts on the document, when the template has one to
// give. Shared by the worker (the read) and the approval (the reviewed read), so
// both always agree on which field the document's expiry comes from.
export function expirationDateFromValues(
  template: DocumentExtractionTemplate,
  values: Readonly<Record<string, unknown>>,
): string | null {
  if (!template.expirationField) return null
  const value = normalizeExtractionValue('date', values[template.expirationField])
  return typeof value === 'string' ? value : null
}
